import React, { useState, useEffect } from 'react';
import { Activity, Database, Cpu, HardDrive, Clock, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { adminAPI } from '../../services/api';
import Card from '../common/Card';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';

const SystemHealth = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { executeApiCall } = useApi();

  useEffect(() => {
    fetchHealthData();
    const interval = setInterval(fetchHealthData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchHealthData = async () => {
    try {
      const result = await executeApiCall(
        () => adminAPI.getSystemHealth()
      );
      
      if (result.success) {
        setHealthData(result.data);
      }
    } catch (err) {
      console.error('Error fetching health data:', err);
      setError('Failed to load system health data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'unhealthy':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Activity className="h-5 w-5 text-ink-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-100';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'unhealthy':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-ink-600 bg-ink-100';
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <Alert type="error" message={error} />;
  }

  if (!healthData) {
    return <Alert type="info" message="No health data available" />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-ink-900">System Health</h1>
        <p className="text-ink-600 mt-2">Real-time system monitoring and performance metrics</p>
      </div>

      {/* Overall Status */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {getStatusIcon(healthData.status)}
            <div>
              <h3 className="text-lg font-semibold text-ink-900">System Status</h3>
              <p className="text-sm text-ink-600">
                Last updated: {new Date(healthData.timestamp).toLocaleString()}
              </p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(healthData.status)}`}>
            {healthData.status.toUpperCase()}
          </div>
        </div>
      </Card>

      {/* System Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Database Health */}
        <Card>
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-100 text-blue-600">
              <Database className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-600">Database</p>
              <p className="text-lg font-semibold text-ink-900">
                {healthData.database.status === 'healthy' ? 'Online' : 'Offline'}
              </p>
              <p className="text-xs text-ink-500">
                {formatBytes(healthData.database.size)}
              </p>
            </div>
          </div>
        </Card>

        {/* Memory Usage */}
        <Card>
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-purple-100 text-purple-600">
              <HardDrive className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-600">Memory</p>
              <p className="text-lg font-semibold text-ink-900">
                {healthData.system.memory.usagePercent}%
              </p>
              <p className="text-xs text-ink-500">
                {healthData.system.memory.used}MB / {healthData.system.memory.total}MB
              </p>
            </div>
          </div>
          {/* Memory Progress Bar */}
          <div className="mt-3">
            <div className="w-full bg-ink-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${
                  healthData.system.memory.usagePercent > 80 ? 'bg-red-500' :
                  healthData.system.memory.usagePercent > 60 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${healthData.system.memory.usagePercent}%` }}
              ></div>
            </div>
          </div>
        </Card>

        {/* CPU Usage */}
        <Card>
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-100 text-green-600">
              <Cpu className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-600">CPU</p>
              <p className="text-lg font-semibold text-ink-900">
                {healthData.system.cpu.usage}%
              </p>
              <p className="text-xs text-ink-500">
                {healthData.system.cpu.cores} cores
              </p>
            </div>
          </div>
          {/* CPU Progress Bar */}
          <div className="mt-3">
            <div className="w-full bg-ink-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${
                  healthData.system.cpu.usage > 80 ? 'bg-red-500' :
                  healthData.system.cpu.usage > 60 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${healthData.system.cpu.usage}%` }}
              ></div>
            </div>
          </div>
        </Card>

        {/* Uptime */}
        <Card>
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-orange-100 text-orange-600">
              <Clock className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-600">Uptime</p>
              <p className="text-lg font-semibold text-ink-900">
                {formatUptime(healthData.system.uptime)}
              </p>
              <p className="text-xs text-ink-500">
                Response: {healthData.responseTime.toFixed(2)}ms
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* System Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-lg font-semibold text-ink-900 mb-4">System Information</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-ink-600">Platform</span>
              <span className="text-sm font-medium text-ink-900">
                {healthData.system.platform}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-ink-600">Node Version</span>
              <span className="text-sm font-medium text-ink-900">
                {healthData.system.nodeVersion}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-ink-600">CPU Cores</span>
              <span className="text-sm font-medium text-ink-900">
                {healthData.system.cpu.cores}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-ink-600">Total Memory</span>
              <span className="text-sm font-medium text-ink-900">
                {healthData.system.memory.total}MB
              </span>
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-ink-900 mb-4">Performance Metrics</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-ink-600">Response Time</span>
              <span className={`text-sm font-medium ${
                healthData.responseTime > 1000 ? 'text-red-600' :
                healthData.responseTime > 500 ? 'text-yellow-600' : 'text-green-600'
              }`}>
                {healthData.responseTime.toFixed(2)}ms
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-ink-600">Memory Usage</span>
              <span className={`text-sm font-medium ${
                healthData.system.memory.usagePercent > 80 ? 'text-red-600' :
                healthData.system.memory.usagePercent > 60 ? 'text-yellow-600' : 'text-green-600'
              }`}>
                {healthData.system.memory.usagePercent}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-ink-600">CPU Usage</span>
              <span className={`text-sm font-medium ${
                healthData.system.cpu.usage > 80 ? 'text-red-600' :
                healthData.system.cpu.usage > 60 ? 'text-yellow-600' : 'text-green-600'
              }`}>
                {healthData.system.cpu.usage}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-ink-600">Database Status</span>
              <span className={`text-sm font-medium ${
                healthData.database.status === 'healthy' ? 'text-green-600' : 'text-red-600'
              }`}>
                {healthData.database.status}
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* Refresh Button */}
      <div className="flex justify-end">
        <button
          onClick={fetchHealthData}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Refresh Data
        </button>
      </div>
    </div>
  );
};

export default SystemHealth;
